<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("release.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-12">
                            <h3 class="mb-0">{{ $t("release.title") }}</h3>
                            <p class="text-muted small mb-0 mt-1">
                                {{ $t("release.subtitle") }}
                            </p>
                        </div>
                    </div>
                </template>

                <div class="px-4 py-3">
                    <div
                        v-for="(r, idx) in rows"
                        :key="r.date + '-' + idx"
                        class="release-entry"
                    >
                        <!-- ===== Header: date + component version chips ===== -->
                        <div class="d-flex flex-wrap align-items-center mb-2">
                            <span class="release-date mr-3">
                                <i class="fa fa-calendar-alt text-muted mr-1"></i>
                                {{ r.date }}
                            </span>
                            <span
                                v-for="chip in r.chips"
                                :key="chip.key"
                                class="mr-2 mb-1"
                            >
                                <el-tag
                                    size="small"
                                    :type="chip.changed ? 'success' : 'info'"
                                    :effect="chip.changed ? 'dark' : 'plain'"
                                    :title="componentDesc(chip.key)"
                                >
                                    <strong>{{ componentLabel(chip.key) }}</strong>
                                    {{ chip.version }}
                                    <i
                                        v-if="chip.changed"
                                        class="fa fa-arrow-up ml-1"
                                    ></i>
                                </el-tag>
                            </span>
                        </div>

                        <!-- ===== Fixes ===== -->
                        <ul class="release-fixes" v-if="r.fixes && r.fixes.length">
                            <li v-for="(f, fi) in r.fixes" :key="fi" class="mb-1">
                                <el-tag
                                    v-if="f.component"
                                    size="mini"
                                    type="info"
                                    class="mr-2"
                                >
                                    {{ f.component }}
                                </el-tag>
                                <span>{{ f.title }}</span>
                                <a
                                    v-if="f.issue"
                                    :href="issueUrl(f.issue)"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="ml-2 issue-link"
                                    :title="'#' + f.issue"
                                >
                                    <i class="fab fa-github"></i>
                                    #{{ f.issue }}
                                </a>
                            </li>
                        </ul>
                        <div v-else class="text-muted small">
                            {{ $t("release.no_fixes") }}
                        </div>

                        <hr v-if="idx < rows.length - 1" class="my-3" />
                    </div>

                    <div v-if="!rows.length" class="text-muted text-center py-4">
                        {{ $t("release.no_data") }}
                    </div>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
import { Tag } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { releases } from "@/content/releases";

export default {
    name: "Releases",
    components: {
        RouteBreadCrumb,
        [Tag.name]: Tag
    },
    data() {
        return {
            // Fixed render order for the component chips.
            componentOrder: ["api", "app", "web", "bo"]
        };
    },
    computed: {
        // Decorate each release with a `changed` map: for a given release,
        // a component is "changed" when its version differs from the SAME
        // component in the next (older) entry. The oldest release is the
        // baseline, so nothing is highlighted there.
        rows() {
            const list = releases || [];
            return list.map((r, i) => {
                const older = list[i + 1];
                const comps = r.components || {};
                const chips = [];
                this.componentOrder.forEach(key => {
                    const cur = comps[key];
                    if (!cur) return; // component not tracked in this release
                    // Changed when the same component in the next (older) entry
                    // has a different version. Oldest entry = baseline (false).
                    const changed =
                        older && older.components
                            ? older.components[key] !== cur
                            : false;
                    chips.push({ key, version: cur, changed });
                });
                return {
                    date: r.date,
                    fixes: r.fixes || [],
                    chips
                };
            });
        }
    },
    methods: {
        issueUrl(n) {
            return "https://github.com/asrth/mdf-project/issues/" + n;
        },
        componentLabel(key) {
            const k = "release.component." + key;
            const label = this.$t(k);
            return label === k ? key.toUpperCase() : label;
        },
        componentDesc(key) {
            const k = "release.component_desc." + key;
            const label = this.$t(k);
            return label === k ? "" : label;
        }
    }
};
</script>

<style scoped>
.release-entry {
    padding: 0.25rem 0;
}
.release-date {
    font-weight: 600;
    font-size: 0.95rem;
}
.release-fixes {
    list-style: none;
    padding-left: 0.25rem;
    margin-bottom: 0;
}
.release-fixes li {
    line-height: 1.6;
}
.issue-link {
    white-space: nowrap;
    font-weight: 600;
}
</style>
